import React from 'react';
import Box from '@mui/material/Box';
import type { AnyBuild } from './buildsApi';
import { isOwnerView, isTerminal } from './buildsApi';
import { duration, secondsBetween, statusOf, type Tone } from './buildMessages';

export const muted = { color: 'var(--ink-3)', fontSize: 12.5 } as const;
export const cardSx = { border: '1px solid var(--line)', borderRadius: '14px', p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: 'var(--bg)', minWidth: 0 } as const;
export const monoSx = { fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)', overflowWrap: 'anywhere' } as const;

const TONES: Record<Tone, string> = { run: 'var(--run)', ok: 'var(--ok)', bad: 'var(--bad)', muted: 'var(--ink-3)' };

export function StatusChip({ build }: { build: AnyBuild }) {
  const { label, tone } = statusOf(build);
  return <Box component="span" data-testid="build-status" data-tone={tone}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', color: TONES[tone],
      '&::before': { content: '""', width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor' } }}>{label}</Box>;
}

export function Bar({ percent, thin }: { percent: number; thin?: boolean }) {
  const p = Math.max(0, Math.min(100, percent));
  return <Box role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}
    sx={{ height: thin ? 4 : 6, borderRadius: 3, bgcolor: 'var(--line-2)', position: 'relative', minWidth: 80, overflow: 'hidden' }}>
    <Box component="i" sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${p}%`, borderRadius: 3, bgcolor: 'var(--acc)', transition: 'width .4s ease' }} />
  </Box>;
}

/** Seconds since the build started, from the server's `as_of` plus the local clock since that reply (display only). */
export function elapsedOf(b: AnyBuild, receivedAt: number, now: number): number | null {
  const base = secondsBetween(b.started_at, b.as_of);
  if (base === null) return null;
  return isTerminal(b) ? secondsBetween(b.started_at, b.finished_at) : base + Math.max(now - receivedAt, 0) / 1000;
}

/** The one progress line of a row or a page: time left when there is an estimate, else elapsed time. */
export function progressLine(b: AnyBuild, elapsed: number | null): string {
  if (!isOwnerView(b)) return '—';
  if (b.state === 'queued') return 'Not started';
  if (isTerminal(b)) return b.state === 'ready' || b.state === 'published' ? 'Done' : '—';
  if (b.estimate) return `about ${duration(b.estimate.left_s)} left${b.estimate.basis === 'slower_than_usual' ? ' · slower than usual' : ''}`;
  return elapsed === null ? 'Starting' : `${duration(elapsed)} so far`;
}

/** Re-render every second while `on` (the elapsed clock); the clock's reading. */
export function useNow(on: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return on ? now : Date.now();
}
